import { useState } from "react";
import { ChevronLeft, ChevronRight, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CHIP_STYLES = {
  new: "border-nitro/50 bg-nitro/15 text-red-200",
  confirmed: "border-amber-400/50 bg-amber-400/10 text-amber-200",
  done: "border-emerald-400/50 bg-emerald-400/10 text-emerald-200",
};

const toKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export const AdminCalendar = ({ bookings, blockedDays, onToggleDay }) => {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const todayKey = toKey(now.getFullYear(), now.getMonth(), now.getDate());

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDate = {};
  (bookings || []).forEach((b) => {
    (byDate[b.preferred_date] = byDate[b.preferred_date] || []).push(b);
  });

  const move = (delta) => setCursor(new Date(year, month + delta, 1));

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold uppercase text-2xl text-neutral-100">
          {MONTHS[month]} <span className="text-nitro">{year}</span>
        </h2>
        <div className="flex gap-2">
          <Button
            data-testid="calendar-prev"
            onClick={() => move(-1)}
            variant="outline"
            className="border-[#262933] bg-transparent hover:bg-panel hover:border-nitro text-neutral-200 rounded-full w-9 h-9 p-0"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            data-testid="calendar-next"
            onClick={() => move(1)}
            variant="outline"
            className="border-[#262933] bg-transparent hover:bg-panel hover:border-nitro text-neutral-200 rounded-full w-9 h-9 p-0"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {DOW.map((d) => (
          <div key={d} className="font-mono text-[9px] tracking-[0.25em] uppercase text-neutral-500 text-center py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((day, i) => {
          if (day === null) return <div key={`blank-${i}`} />;
          const key = toKey(year, month, day);
          const dayBookings = byDate[key] || [];
          const blocked = blockedDays.includes(key);
          const isPast = key < todayKey;
          return (
            <button
              key={key}
              data-testid={`calendar-day-${key}`}
              onClick={() => !isPast && onToggleDay(key)}
              disabled={isPast}
              className={`min-h-[70px] sm:min-h-[88px] rounded-lg border p-1.5 text-left align-top transition-colors duration-200 ${
                blocked
                  ? "border-nitro/60 bg-nitro/10"
                  : "border-[#1F232D] bg-[#0E1015]"
              } ${isPast ? "opacity-35 cursor-default" : "hover:border-nitro/60 cursor-pointer"} ${
                key === todayKey ? "ring-1 ring-nitro" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-mono text-[10px] ${key === todayKey ? "text-nitro font-bold" : "text-neutral-400"}`}>
                  {day}
                </span>
                {blocked && (
                  <span className="flex items-center gap-1 font-mono text-[8px] tracking-[0.15em] uppercase text-nitro">
                    <Ban size={9} /> Off
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-col gap-1">
                {dayBookings.slice(0, 2).map((b) => (
                  <span
                    key={b.booking_id}
                    className={`truncate rounded border px-1 py-0.5 text-[9px] font-mono leading-tight ${CHIP_STYLES[b.status] || CHIP_STYLES.new}`}
                    title={`${b.time_slot} — ${b.full_name} — ${b.vehicle}`}
                  >
                    {b.time_slot} {b.full_name.split(" ")[0]}
                  </span>
                ))}
                {dayBookings.length > 2 && (
                  <span className="text-[9px] font-mono text-neutral-500 px-1">
                    +{dayBookings.length - 2} more
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-4 font-mono text-[9px] tracking-[0.2em] uppercase text-neutral-600">
        Tap a future day to block or unblock it — days with bookings still show their jobs
      </p>
    </div>
  );
};
